import React, { useState, useEffect, useRef } from 'react';
import axios from '@/utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Shield, Users, CheckCircle, XCircle, Clock, UserPlus } from 'lucide-react';

const RoleManagement = () => {
  const { token } = useAuth();
  const { isSuperAdmin, loading: permissionsLoading } = usePermissions();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [sousAdmins, setSousAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!permissionsLoading && !isSuperAdmin()) {
      toast.error('Accès non autorisé');
      window.location.href = '/dashboard';
      return;
    }

    if (!permissionsLoading) {
      fetchData();
    }
  }, [permissionsLoading]);

  const fetchData = async () => {
    try {
      if (isMountedRef.current) setLoading(true);
      
      // Fetch pending approvals
      const pendingResponse = await axios.get(`${API}/users/pending-approval`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) setPendingUsers(pendingResponse.data);

      // Fetch all employees
      const usersResponse = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (isMountedRef.current) {
        const allUsers = usersResponse.data;
        const employeesList = allUsers.filter(u => u.role === 'employee');
        const sousAdminsList = employeesList.filter(u => u.employee_roles?.includes('sous_admin'));
        
        setEmployees(employeesList);
        setSousAdmins(sousAdminsList);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (isMountedRef.current) toast.error('Erreur lors du chargement des données');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const handleApprove = async (userId, approved) => {
    try {
      await axios.put(
        `${API}/users/${userId}/approve`,
        null,
        {
          params: { approved },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(approved ? 'Employé approuvé avec succès' : 'Employé rejeté');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'approbation');
    }
  };

  const handleAssignEmployees = async () => {
    if (!selectedAdmin || selectedEmployees.length === 0) {
      toast.error('Veuillez sélectionner un sous-admin et au moins un employé');
      return;
    }

    try {
      await axios.put(
        `${API}/users/${selectedAdmin}/assign-employees`,
        selectedEmployees,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast.success(`${selectedEmployees.length} employé(s) assigné(s) avec succès`);
      setSelectedAdmin('');
      setSelectedEmployees([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    }
  };

  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => 
      prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId]
    );
  };

  const getRoleBadgeColor = (roles) => {
    if (!roles || roles.length === 0) return 'bg-gray-500';
    const role = roles[0];
    const colors = {
      'sous_admin': 'bg-purple-600',
      'comptabilite': 'bg-blue-600',
      'controleur': 'bg-green-600',
      'conseiller': 'bg-yellow-600',
      'assistant': 'bg-pink-600'
    };
    return colors[role] || 'bg-gray-500';
  };

  const getRoleLabel = (roles) => {
    if (!roles || roles.length === 0) return 'Aucun rôle';
    const role = roles[0];
    const labels = {
      'sous_admin': 'Sous-Admin',
      'comptabilite': 'Comptabilité',
      'controleur': 'Contrôleur',
      'conseiller': 'Conseiller',
      'assistant': 'Assistant'
    };
    return labels[role] || role;
  };

  if (permissionsLoading || loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Chargement...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="h-8 w-8 text-red-600" />
              Gestion des Rôles et Permissions
            </h1>
            <p className="text-gray-600 mt-1">Administration des employés et des rôles</p>
          </div>
        </div>

        {/* Pending Approvals */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              Approbations en Attente ({pendingUsers.length})
            </CardTitle>
            <CardDescription>
              Employés créés par les sous-administrateurs en attente de votre approbation
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pendingUsers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Aucune approbation en attente</p>
            ) : (
              <div className="space-y-3">
                {pendingUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg bg-orange-50">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{user.full_name}</p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <div className="flex gap-2 mt-2">
                        <Badge className={getRoleBadgeColor(user.employee_roles)}>
                          {getRoleLabel(user.employee_roles)}
                        </Badge>
                        <Badge variant="outline">
                          Créé par: {user.created_by || 'N/A'}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApprove(user.id, true)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approuver
                      </Button>
                      <Button
                        onClick={() => handleApprove(user.id, false)}
                        variant="destructive"
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Rejeter
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assign Employees to Sous-Admin */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-600" />
              Assigner des Employés aux Sous-Administrateurs
            </CardTitle>
            <CardDescription>
              Assignez des employés à un sous-administrateur pour qu'il puisse les gérer
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Select Sous-Admin */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionner un Sous-Administrateur
                </label>
                <select
                  value={selectedAdmin}
                  onChange={(e) => setSelectedAdmin(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">-- Choisir un sous-admin --</option>
                  {sousAdmins.map(admin => (
                    <option key={admin.id} value={admin.id}>
                      {admin.full_name} ({admin.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Select Employees */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sélectionner les Employés à Assigner
                </label>
                <div className="border rounded-lg max-h-64 overflow-y-auto">
                  {employees
                    .filter(emp => !emp.employee_roles?.includes('sous_admin'))
                    .map(employee => (
                      <div
                        key={employee.id}
                        onClick={() => toggleEmployeeSelection(employee.id)}
                        className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                          selectedEmployees.includes(employee.id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{employee.full_name}</p>
                            <p className="text-sm text-gray-600">{employee.email}</p>
                          </div>
                          <div className="flex gap-2 items-center">
                            <Badge className={getRoleBadgeColor(employee.employee_roles)}>
                              {getRoleLabel(employee.employee_roles)}
                            </Badge>
                            {selectedEmployees.includes(employee.id) && (
                              <CheckCircle className="h-5 w-5 text-blue-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Summary and Action */}
              {selectedEmployees.length > 0 && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">
                    {selectedEmployees.length} employé(s) sélectionné(s)
                  </p>
                </div>
              )}

              <Button
                onClick={handleAssignEmployees}
                disabled={!selectedAdmin || selectedEmployees.length === 0}
                className="w-full"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Assigner les Employés
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default RoleManagement;
